import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormControl, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { catchError, combineLatest, of, Subscription, tap } from 'rxjs';

import { DialogService } from 'primeng/dynamicdialog';

import { AuthService } from '../../../services/auth.service';
import { ConfirmationModalComponent } from '../../../core/components/confirmation-modal/confirmation-modal.component';
import { CurrentUserService } from '../../../shared/services/current-user.service';
import { SecurityProfileModel } from '../../../loan-state/security-profile/security-profile.model';
import { SecurityProfileQuery } from '../../../loan-state/security-profile/security-profile.query';
import { SecurityProfileStore } from '../../../loan-state/security-profile/security-profile.store';
import { SessionStore } from '../../../core/state/session/session.store';
import { ButtonDirective, Button } from 'primeng/button';
import { VersionNumberComponent } from '../version-number/version-number.component';
import { AsyncPipe } from '@angular/common';
import { RadioButton } from 'primeng/radiobutton';
import { LoginFooterComponent } from '../login-footer/login-footer.component';

@Component({
  selector: 'cmg-clear2-ui-security-profiles',
  templateUrl: './security-profiles.component.html',
  styleUrls: ['./security-profiles.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ButtonDirective, VersionNumberComponent, ReactiveFormsModule, FormsModule, RadioButton, Button, LoginFooterComponent, AsyncPipe],
})
export class SecurityProfilesComponent implements OnInit, OnDestroy {
  private readonly _router = inject(Router);
  private readonly _securityProfileStore = inject(SecurityProfileStore);
  private readonly _securityProfileQuery = inject(SecurityProfileQuery);
  private readonly _currentUserService = inject(CurrentUserService);
  private readonly _sessionStore = inject(SessionStore);
  private readonly _authService = inject(AuthService);
  private readonly _dialogService = inject(DialogService);
  private readonly _cdRef = inject(ChangeDetectorRef);

  displaySpinner = true;
  securityProfiles$ = this._securityProfileQuery.selectAll().pipe(
    tap((securityProfiles) => {
      securityProfiles.sort((a: any, b: any) => {
        if (a.businessChannel === b.businessChannel) {
          let _return = 0;
          if (a.securityProfileName < b.securityProfileName) {
            _return = -1;
          } else if (a.securityProfileName > b.securityProfileName) {
            _return = 1;
          }
          return _return;
        }
        return a.businessChannel > b.businessChannel ? 1 : -1;
      });

      setTimeout(() => {
        this.displaySpinner = false;
        this._cdRef.detectChanges();
      }, 1000);
    }),
  );
  securityProfileControl = new FormControl<SecurityProfileModel>(null);

  private _subscription: Subscription;

  ngOnInit(): void {
    this._subscription = combineLatest([
      this._currentUserService.getSecurityProfiles().pipe(catchError(this._catchError)),
      this._currentUserService.getCurrentUserInfo().pipe(catchError(this._catchError)),
    ]).subscribe(([securityProfileResp, currentUserInfoResp]) => {
      if (securityProfileResp !== null && securityProfileResp.profiles?.length > 0 && currentUserInfoResp !== null) {
        securityProfileResp.profiles.forEach((p: SecurityProfileModel) => {
          this._currentUserService.convertFeaturePermissions(p);
        });

        this._securityProfileStore.set(securityProfileResp.profiles);

        this._sessionStore.update({
          firstName: currentUserInfoResp.firstName,
          lastName: currentUserInfoResp.lastName,
          email: currentUserInfoResp.email,
          nmlsid: currentUserInfoResp.nmlsid,
        });

        if (securityProfileResp.profiles.length === 1) {
          this.selectSecurityProfile(securityProfileResp.profiles[0]);
        }
      } else {
        const dialogRef = this._dialogService.open(ConfirmationModalComponent, {
          closable: false,
          width: '500px',
          data: {
            message: `Please proceed to close your session and log in again.`,
            header: 'Something went wrong',
            icon: 'pi pi-exclamation-triangle',
            rejectLabel: null,
            acceptLabel: 'Close Session',
            acceptIcon: 'pi pi-none',
          },
        });
        dialogRef.onClose.subscribe(() => {
          this._authService.logout();
        });
      }
    });
  }

  ngOnDestroy(): void {
    this._subscription?.unsubscribe();
  }

  selectSecurityProfile(profile?): void {
    const saveData = profile || this.securityProfileControl.value;
    this._securityProfileStore.setActive(saveData.userOrgProfileId);
    this._sessionStore.update({
      userId: saveData.userId,
      userName: saveData.userName,
    });
    this._authService
      .createSecurityProfileAuthToken()
      .pipe(
        tap(() => {
          this._router.navigateByUrl('/');
        }),
      )
      .subscribe();
  }

  trackByUserOrgProfileId(index: number, securityProfile: SecurityProfileModel) {
    return securityProfile.userOrgProfileId;
  }

  logout() {
    this._authService.logout();
  }

  private _catchError(errorResponse: HttpErrorResponse) {
    // eslint-disable-next-line
    console.error('Security Profiles Error: ', errorResponse);
    return of(null);
  }
}
